import { Facebook, Instagram, Mail, MessageCircle, Phone, Youtube } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";
import { SITE } from "@/data/content";

const SOCIALS = [
  { key: "youtube", url: SITE.youtube, Icon: Youtube },
  { key: "instagram", url: SITE.instagram, Icon: Instagram },
  { key: "facebook", url: SITE.facebook, Icon: Facebook },
].filter((s) => s.url);

export const ContactDialog = ({ open, onClose }) => {
  const { t } = useLang();
  const actions = [
    { key: "call", label: t.contact.call, value: SITE.phone, href: SITE.phoneHref, Icon: Phone, primary: true },
    { key: "whatsapp", label: t.contact.whatsapp, value: SITE.phone, href: `${SITE.whatsapp}?text=${encodeURIComponent(t.contact.whatsappText)}`, Icon: MessageCircle },
    { key: "email", label: t.contact.email, value: SITE.email, href: `mailto:${SITE.email}`, Icon: Mail },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="contact-dialog"
        className="inset-x-0 bottom-0 top-auto block w-full max-w-none translate-x-0 translate-y-0 rounded-t-2xl border-line bg-coal p-7 text-cream shadow-2xl data-[state=closed]:slide-out-to-bottom-4 data-[state=open]:slide-in-from-bottom-4 data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100 sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:max-w-[calc(100vw-3rem)] md:max-w-lg sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-md sm:p-10 sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%] sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%] [&>button]:right-4 [&>button]:top-4 [&>button]:rounded-full [&>button]:border [&>button]:border-line [&>button]:bg-black/60 [&>button]:p-2 [&>button]:text-cream [&>button]:opacity-100 [&>button]:hover:border-ember [&>button]:hover:text-ember"
      >
        <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-ember">
          <span className="h-px w-8 bg-ember" aria-hidden="true" />
          {t.contact.eyebrow}
        </p>
        <DialogTitle className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight text-cream md:text-4xl">
          {t.contact.title}
        </DialogTitle>
        <DialogDescription className="mt-3 text-sm leading-relaxed text-ash md:text-base">{t.contact.description}</DialogDescription>

        <ul className="mt-8 space-y-3">
          {actions.map(({ key, label, value, href, Icon, primary }) => (
            <li key={key}>
              <a
                href={href}
                target={key === "whatsapp" ? "_blank" : undefined}
                rel={key === "whatsapp" ? "noopener noreferrer" : undefined}
                data-testid={`contact-${key}`}
                className={`group flex items-center gap-4 rounded-md border p-4 transition-colors duration-300 ${
                  primary ? "border-ember/70 bg-ember/10 hover:bg-ember/20" : "border-line bg-card2 hover:border-ember/60"
                }`}
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ember text-ink">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[10px] font-semibold uppercase tracking-[0.25em] text-ash">{label}</span>
                  <span className="mt-0.5 block truncate font-display text-base font-bold text-cream group-hover:text-emberHover md:text-lg">{value}</span>
                </span>
              </a>
            </li>
          ))}
        </ul>

        <div className="mt-8 flex items-center gap-3 border-t border-line pt-6">
          <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ash">{t.contact.follow}</span>
          {SOCIALS.map(({ key, url, Icon }) => (
            <a
              key={key}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={key}
              data-testid={`contact-social-${key}`}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-ash transition-colors duration-300 hover:border-ember hover:text-ember"
            >
              <Icon className="h-4 w-4" />
            </a>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
